/**
 * GCSE 9–1 ↔ A*–G in England.
 *
 * Sources (retrieved October 2026):
 * - Department for Education, "Do you use GCSE grades to assess applicants to your business,
 *   college or university?" (May 2019), the factsheet for employers and FE and HE providers —
 *   https://assets.publishing.service.gov.uk/media/5cd2e9efed915d7892a83e71/GCSE_factsheet_for_employers_FE_and_HE_providers__final_.pdf
 *   Its grade-scale diagram pairs 9 and 8 with A*, 7 with A, 6 and 5 with B, 4 with C, 3 with D,
 *   2 with E and F, 1 with G. It also states the anchor points (the bottom of 7 is aligned with
 *   the bottom of A, the bottom of 4 with the bottom of C, the bottom of 1 with the bottom of G),
 *   that grade 4 or above is the DfE "standard pass" and that grade 5 or above in English or
 *   maths is its "strong pass".
 * - GOV.UK, "GCSE 9 to 1 grade scale explained" (30 June 2025), the same three anchor points,
 *   and that the number scale is not directly equivalent to the letter one.
 * - Ofqual / GOV.UK, "How the top grades will be awarded in new GCSEs in 2017" (7 September
 *   2016): about 20% of all grades at 7 or above are a 9, and the 8 boundary is equally spaced
 *   between the 7 and 9 boundaries.
 *
 * The pairing below is a comparison, not an equivalence: Ofqual says plainly the scales are not
 * directly equivalent, and only the three anchor points are fixed.
 */

export type NewGrade = 9 | 8 | 7 | 6 | 5 | 4 | 3 | 2 | 1 | 0;
export type OldGrade = "A*" | "A" | "B" | "C" | "D" | "E" | "F" | "G" | "U";

/** 0 stands for U (ungraded). */
export const NEW_GRADES: readonly NewGrade[] = [9, 8, 7, 6, 5, 4, 3, 2, 1, 0];
export const OLD_GRADES: readonly OldGrade[] = [
  "A*",
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "U",
];

/** DfE diagram, new grade → the old grades it sits beside. */
const NEW_TO_OLD: Record<NewGrade, readonly OldGrade[]> = {
  9: ["A*"],
  8: ["A*"],
  7: ["A"],
  6: ["B"],
  5: ["B"],
  4: ["C"],
  3: ["D"],
  2: ["E", "F"],
  1: ["G"],
  0: ["U"],
};

/** DfE diagram, old grade → the new grades it sits beside. */
const OLD_TO_NEW: Record<OldGrade, readonly NewGrade[]> = {
  "A*": [9, 8],
  A: [7],
  B: [6, 5],
  C: [4],
  D: [3],
  E: [2],
  F: [2],
  G: [1],
  U: [0],
};

/** The three Ofqual anchor points: [new grade, old grade] aligned at the bottom of the grade. */
export const ANCHOR_POINTS: readonly (readonly [NewGrade, OldGrade])[] = [
  [7, "A"],
  [4, "C"],
  [1, "G"],
];

export type PassLevel = "strong" | "standard" | "below";

/** DfE: 4 and above is a standard pass; 5 and above is a strong pass (English and maths). */
export function passLevel(grade: NewGrade): PassLevel {
  if (grade >= 5) {
    return "strong";
  }
  if (grade === 4) {
    return "standard";
  }
  return "below";
}

export function oldGradesFor(grade: NewGrade): readonly OldGrade[] {
  return NEW_TO_OLD[grade];
}

export function newGradesFor(grade: OldGrade): readonly NewGrade[] {
  return OLD_TO_NEW[grade];
}

export function isAnchor(grade: NewGrade): boolean {
  return ANCHOR_POINTS.some(([value]) => value === grade);
}

export function isOldAnchor(grade: OldGrade): boolean {
  return ANCHOR_POINTS.some(([, value]) => value === grade);
}

export interface ResultsSummary {
  /** Grades read from the text, best first. */
  grades: NewGrade[];
  atLeast4: number;
  atLeast5: number;
  atLeast7: number;
  /** Tokens that were not a grade from 9 to 1 or U. */
  ignored: string[];
}

const SEPARATORS = /[\s,;]+/;
const SINGLE_GRADE = /^[1-9]$/;

/** Reads "7 6 6 5 4 4 3" (or commas) as 9–1 grades; U counts as ungraded. */
export function summariseResults(text: string): ResultsSummary {
  const grades: NewGrade[] = [];
  const ignored: string[] = [];
  for (const token of text.trim().split(SEPARATORS)) {
    if (token === "") {
      continue;
    }
    if (SINGLE_GRADE.test(token)) {
      grades.push(Number(token) as NewGrade);
    } else if (token.toUpperCase() === "U") {
      grades.push(0);
    } else {
      ignored.push(token);
    }
  }
  grades.sort((a, b) => b - a);
  return {
    grades,
    atLeast4: grades.filter((grade) => grade >= 4).length,
    atLeast5: grades.filter((grade) => grade >= 5).length,
    atLeast7: grades.filter((grade) => grade >= 7).length,
    ignored,
  };
}
